import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { businessDaysBetween, calendarDaysBetween, todayISO } from "../lib/dates";
import { changeJobStatus } from "../lib/jobStatus";
import { Tag } from "../components/Tag";
import { ChangeSummary } from "../components/ChangeSummary";
import { isNewJob, isRecentlyClosedJob } from "../lib/tags";
import { PIPELINE_STATUSES, STAGE_GROUPS, stageOf, type Company, type Job, type JobStatus } from "../lib/types";

/** Row highlight: blue for "Under consideration", green while actively interviewing. */
function rowClass(status: JobStatus): string {
  if (status === "Under consideration") return "row-blue";
  if (stageOf(status) === "Interviews") return "row-green";
  return "";
}

type Pending = { state: JobStatus | ""; date: string };

/** Date the job entered its current state; falls back to the application date. */
function stateSince(job: Job): string | null {
  return job.state_changed_at || job.application_date || null;
}

function normalizeUrl(url: string): string {
  return url.trim().toLowerCase().replace(/[?#].*$/, "").replace(/\/+$/, "");
}

/** Company-reported status is only worth showing when it isn't one of our own states. */
function isForeignStatus(status: string): boolean {
  const s = status.trim().toLowerCase();
  return s !== "" && !PIPELINE_STATUSES.some((h) => h.toLowerCase() === s);
}

export function ApplicationsPage() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, Pending>>({});

  const load = useCallback(async () => {
    if (!user) return;
    const [cRes, jRes] = await Promise.all([
      supabase.from("jh_companies").select("*").eq("user_id", user.id),
      supabase.from("jh_jobs").select("*").eq("user_id", user.id).eq("applied", true),
    ]);
    if (cRes.error) setError(cRes.error.message);
    else setCompanies((cRes.data ?? []) as Company[]);
    if (jRes.error) setError(jRes.error.message);
    else setJobs((jRes.data ?? []) as Job[]);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const companyById = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);

  const rows = useMemo(() => {
    const seen = new Set<string>();
    return jobs
      .filter((j) => PIPELINE_STATUSES.includes(j.status))
      .filter((j) => {
        // One row per job description (keyed by req link, falling back to the job id).
        const key = j.url ? normalizeUrl(j.url) : j.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        const s = PIPELINE_STATUSES.indexOf(a.status) - PIPELINE_STATUSES.indexOf(b.status);
        if (s !== 0) return s;
        const c = (companyById.get(a.company_id)?.name ?? "").localeCompare(
          companyById.get(b.company_id)?.name ?? "",
        );
        if (c !== 0) return c;
        return (a.application_date || "").localeCompare(b.application_date || "");
      });
  }, [jobs, companyById]);

  async function moveToState(job: Job) {
    if (!user) return;
    const p = pending[job.id];
    if (!p?.state) return;
    const { patch, error: err } = await changeJobStatus(user.id, job, p.state, p.date || todayISO());
    if (err) setError(err);
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, ...patch } : j)));
    setPending((prev) => {
      const next = { ...prev };
      delete next[job.id];
      return next;
    });
  }

  async function saveCompanyStatus(job: Job, value: string) {
    const next = value.trim();
    if (next === (job.company_status ?? "")) return;
    const { error: err } = await supabase
      .from("jh_jobs")
      .update({ company_status: next, updated_at: new Date().toISOString() })
      .eq("id", job.id);
    if (err) setError(err.message);
    else setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, company_status: next } : j)));
  }

  async function setDashboardUrl(company: Company) {
    const url = window.prompt(`Dashboard URL for ${company.name}`, company.dashboard_url ?? "");
    if (url === null) return;
    const { error: err } = await supabase
      .from("jh_companies")
      .update({ dashboard_url: url.trim() })
      .eq("id", company.id);
    if (err) setError(err.message);
    else
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, dashboard_url: url.trim() } : c)),
      );
  }

  const today = todayISO();

  return (
    <div className="panel">
      <h2>Applications</h2>
      <ChangeSummary jobs={jobs} />
      <p className="muted">
        {rows.length} active application{rows.length === 1 ? "" : "s"} · sorted by status,
        company, date applied
      </p>
      {error ? <p className="error">{error}</p> : null}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Stage</th>
              <th>Status</th>
              <th>Company</th>
              <th>Job</th>
              <th>Applied</th>
              <th>Days in state</th>
              <th>Move to next state</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  No active applications.
                </td>
              </tr>
            ) : null}
            {rows.map((j) => {
              const company = companyById.get(j.company_id);
              const since = stateSince(j);
              const p: Pending = pending[j.id] ?? { state: "", date: today };
              return (
                <tr key={j.id} className={rowClass(j.status)}>
                  <td>{stageOf(j.status)}</td>
                  <td>{j.status}</td>
                  <td>
                    {company?.dashboard_url ? (
                      <a href={company.dashboard_url} target="_blank" rel="noreferrer">
                        {company.name}
                      </a>
                    ) : (
                      <>
                        {company?.name ?? "(unknown)"}{" "}
                        {company ? (
                          <button
                            type="button"
                            className="linkish"
                            onClick={() => void setDashboardUrl(company)}
                          >
                            add dashboard link
                          </button>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td>
                    {j.url ? (
                      <a href={j.url} target="_blank" rel="noreferrer">
                        {j.title}
                      </a>
                    ) : (
                      j.title
                    )}
                    {isNewJob(j) ? <Tag kind="new" /> : null}
                    {isRecentlyClosedJob(j) ? <Tag kind="closed" /> : null}
                    {j.requisition ? <div className="muted">Req {j.requisition}</div> : null}
                  </td>
                  <td>{j.application_date || "—"}</td>
                  <td>
                    {since ? (
                      <>
                        {calendarDaysBetween(since, today)} calendar
                        <div className="muted">{businessDaysBetween(since, today)} business</div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <select
                      value={p.state}
                      onChange={(e) =>
                        setPending((prev) => ({
                          ...prev,
                          [j.id]: { ...p, state: e.target.value as JobStatus | "" },
                        }))
                      }
                    >
                      <option value="">Select…</option>
                      {STAGE_GROUPS.filter((g) => g.stage !== "Not applied").map((g) => (
                        <optgroup key={g.stage} label={g.stage}>
                          {g.statuses.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>{" "}
                    <input
                      type="date"
                      value={p.date}
                      max={today}
                      onChange={(e) =>
                        setPending((prev) => ({ ...prev, [j.id]: { ...p, date: e.target.value } }))
                      }
                    />{" "}
                    <button
                      type="button"
                      className="btn primary"
                      disabled={!p.state}
                      onClick={() => void moveToState(j)}
                    >
                      Move
                    </button>
                  </td>
                  <td>
                    <input
                      defaultValue={j.company_status ?? ""}
                      placeholder="Status on company dashboard"
                      onBlur={(e) => void saveCompanyStatus(j, e.target.value)}
                    />
                    {isForeignStatus(j.company_status ?? "") ? (
                      <div className="muted">Company state: {j.company_status}</div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
